/*
 * The sub-inputs of x-input (components/input/*). Each is an Alpine component with x-modelable, so a
 * Livewire form binds it with wire:model like a plain field: no extra component or event in between.
 */
import { layers, openable } from './alpine.js';
import { format as formatText } from './format.js';   // what's typed into a list that takes it
import { changed } from './unsaved.js';   // each tells its form when the visitor changes it
import { json } from './upload.js';         // a JSON request with the CSRF token
import { initEditor, openDialog } from './ckeditor.js';   // the Markdown editor
import { allowed, position } from './permissions.js';   // this device's position, a card before the browser's own prompt
import signatureLatin from '@fontsource/dancing-script/files/dancing-script-latin-400-normal.woff2?url';
import signatureLatinExt from '@fontsource/dancing-script/files/dancing-script-latin-ext-400-normal.woff2?url';

// The phone library is large, so it loads only on pages with a phone field, once.
let phoneNumbers = null;

// A phone number as +353871234567: a country picker (flag and code) and the number, formatted as it's typed.
Alpine.data('phoneInput', (fallback, dialled) => ({
    value: '',
    country: dialled.includes(fallback) ? fallback : 'IE',
    national: '',

    async init() {
        phoneNumbers ??= await import('libphonenumber-js/min');
        this.read(this.value);
        this.$watch('value', (value) => value !== this.sent && this.read(value));
        this.$watch('country', () => this.update());
    },

    read(value) {
        const number = value ? phoneNumbers.parsePhoneNumberFromString(value) : null;

        if (number?.country && dialled.includes(number.country)) {
            this.country = number.country;
        }
        this.national = number ? number.formatNational() : (value ?? '');
    },

    update() {
        // Formats as typed, unless the person is deleting (it would put back what they removed)
        if (!this.national.endsWith(' ') && this.national.length > (this.typed ?? '').length) {
            this.national = new phoneNumbers.AsYouType(this.country).input(this.national);
        }
        this.typed = this.national;

        const number = phoneNumbers.parsePhoneNumberFromString(this.national, this.country);
        this.value = this.sent = number ? number.number : this.national.trim();
    },

    tidy() {
        const number = phoneNumbers.parsePhoneNumberFromString(this.national, this.country);

        if (number?.isValid()) {
            this.national = number.formatNational();
        }
    },
}));

// An amount kept in cents ("1250"), shown with the visitor's decimal mark (12.50 or 12,50). Either mark
// is understood when typed: the last one is the decimal mark, any others are thousands.
// Tags as pills (components/input/tags): the value is one comma-separated text, "cob, vanner"
Alpine.data('tagsInput', () => ({
    value: '',
    text: '',
    said: '',

    get tags() {
        return String(this.value ?? '').split(',').map((tag) => tag.trim()).filter(Boolean);
    },

    /** What was typed (or pasted), each comma-separated part a tag; one already there (any case) isn't added twice */
    add(text = this.text) {
        const tags = this.tags;
        const added = text.split(/[,\n]/).map((tag) => tag.trim().replace(/\s+/g, ' ')).filter((tag, index, all) => tag
            && ![...tags, ...all.slice(0, index)].some((other) => other.toLowerCase() === tag.toLowerCase()));
        this.text = '';
        if (added.length) {
            this.value = [...tags, ...added].join(', ');
            this.said = `Added ${added.join(', ')}`;
        }
    },

    remove(tag) {
        this.value = this.tags.filter((other) => other !== tag).join(', ');
        this.said = `Removed ${tag}`;
        this.$refs.input.focus();
    },

    key(event) {
        if (event.key === ',') {
            event.preventDefault();
            this.add();
        } else if (event.key === 'Backspace' && this.text === '' && this.tags.length) {
            this.remove(this.tags.at(-1));
        }
    },

    /** A list pasted in ("cob, vanner") becomes its tags; a single word is just typed */
    paste(event) {
        const text = event.clipboardData?.getData('text') ?? '';
        if (/[,\n]/.test(text)) {
            event.preventDefault();
            this.add(this.text + text);
        }
    },
}));

Alpine.data('moneyInput', (decimal = '.') => ({
    cents: '',
    amount: '',

    init() {
        this.read(this.cents);
        this.$watch('cents', (cents) => cents !== this.sent && this.read(cents));
    },

    read(cents) {
        this.amount = cents === '' || cents === null ? '' : (Number(cents) / 100).toFixed(2).replace('.', decimal);
    },

    update() {
        this.amount = this.amount.replace(/[^\d.,]/g, '');
        const at = Math.max(this.amount.lastIndexOf('.'), this.amount.lastIndexOf(','));
        const whole = (at < 0 ? this.amount : this.amount.slice(0, at)).replace(/\D/g, '');
        const fraction = at < 0 ? '' : this.amount.slice(at + 1).replace(/\D/g, '');
        this.cents = this.sent = whole + fraction === '' ? '' : String(Math.round(parseFloat(`${whole || 0}.${fraction || 0}`) * 100));
    },

    tidy() {
        this.read(this.cents);
    },
}));

// A whole number with − and + (components/input/stepper): typed or a step at a time, kept between min and max
Alpine.data('stepperInput', (min = 0, max = null, step = 1) => ({
    value: null,
    min,
    max,

    change(by) {
        this.value = this.fit((Number(this.value) || 0) + by * step);
    },

    /** Left, it's a whole number in range again (empty stays empty) */
    tidy() {
        this.value = this.value === '' || this.value === null ? this.value : this.fit(Math.round(Number(this.value)));
    },

    fit(number) {
        return Math.min(max ?? Infinity, Math.max(min, number));
    },
}));

// A drawn signature as SVG markup, drawn on a pad in a dialog (signature_pad, loaded when needed). Every change,
// Clear too, can be undone: `past` holds {strokes, value} as they were.
const PEN = '#171717';   // the ink: a drawing's and a typed name's

Alpine.data('signatureInput', (account = null) => ({   // account: where the saved signature is fetched and kept (signed in)
    value: '',
    keep: false,        // Save as My Signature, ticked by the signer
    savedValue: null,   // the saved signature once fetched or kept
    signaturePad: null,
    past: [],
    drawnWidth: 0,
    swipe: null,
    swiped: false,
    mode: 'draw',   // or 'type': the name typed, in a handwriting font, for anyone who can't draw it
    name: '',
    fonts: null,    // Dancing Script's files as base64 (Western and Central European letters), fetched when Type is chosen
    edited: false,  // changed while the pad is open: its form hears once, when the pad closes (a self-saving form
                    // would otherwise save on every stroke and letter)
    warned: 0,      // when Type Your Name last said a character isn't allowed

    // A sideways swipe on the field opens the pad, as signing on it would (up and down still scroll the page)
    swipeStart(event) {
        this.swipe = { x: event.clientX, y: event.clientY };
    },

    swipeMove(event) {
        const dx = this.swipe ? Math.abs(event.clientX - this.swipe.x) : 0;

        if (dx > 10 && dx > Math.abs(event.clientY - this.swipe.y)) {
            this.swipe = null;
            event.currentTarget.click();
            // the click a touch can still send when the finger lifts would close the pad again
            this.swiped = true;
            setTimeout(() => this.swiped = false, 800);
        }
    },

    async pad(canvas) {
        await window.loadSignaturePad();
        // Ink like a pen's: thin when quick, fuller when slow
        this.signaturePad = new SignaturePad(canvas, { penColor: PEN, minWidth: 0.8, maxWidth: 2.8 });
        this.signaturePad.addEventListener('beginStroke', () => this.remember());
        this.signaturePad.addEventListener('endStroke', () => this.sync());

        // The pad has no size until its dialog opens; strokes are rescaled whenever it changes
        new ResizeObserver(() => this.resize(canvas)).observe(canvas);
    },

    resize(canvas) {
        const width = canvas.offsetWidth;

        if (!width) {
            return;
        }

        const factor = this.drawnWidth ? width / this.drawnWidth : 1;
        const scale = (strokes) => strokes.map((stroke) => ({
            ...stroke,
            points: stroke.points.map((point) => ({ ...point, x: point.x * factor, y: point.y * factor })),
        }));
        const strokes = scale(this.signaturePad.toData());
        this.past = this.past.map((state) => ({ ...state, strokes: scale(state.strokes) }));
        this.drawnWidth = width;

        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        canvas.width = width * ratio;
        canvas.height = canvas.offsetHeight * ratio;
        canvas.getContext('2d').scale(ratio, ratio);
        this.signaturePad.fromData(strokes);
    },

    // A saved signature shows on the pad until something is drawn (the pad can't take strokes back from SVG)
    get saved() {
        return this.value && this.signaturePad?.isEmpty();
    },

    state() {
        return { strokes: this.signaturePad.toData(), value: this.value };
    },

    remember() {
        this.past.push(this.state());
    },

    restore(state) {
        this.signaturePad.fromData(state.strokes);
        this.value = state.value;
        this.edited = true;
    },

    // The pad closed (Done, ×, Escape, a click outside): its form hears of any change now
    closed() {
        if (this.edited) {
            this.edited = false;
            changed(this.$root);
        }
    },

    undo() {
        if (this.past.length) {
            this.restore(this.past.pop());
        }
    },

    clear() {
        if (this.value) {
            this.remember();
            this.restore({ strokes: [], value: '' });
        }
    },

    // Use My Signature: true once it's on the pad
    async useSaved() {
        this.savedValue = (await json(account.get).catch(() => ({}))).signature ?? null;

        if (!this.savedValue) {
            window.Toaster?.error('Your saved signature couldn\'t be loaded. Sign here instead.');

            return false;
        }

        this.remember();
        this.restore({ strokes: [], value: this.savedValue });

        return true;
    },

    // Done: with Save as My Signature ticked, kept on the account
    async done() {
        if (!account || !this.keep || !this.value || this.value === this.savedValue) {
            return;
        }

        try {
            await json(account.save, { method: 'POST', body: JSON.stringify({ signature: this.value }) });
            [this.savedValue, this.keep] = [this.value, false];
            window.Toaster?.success('Saved as your signature.');
        } catch {
            window.Toaster?.error('Your signature couldn\'t be saved to your account.');
        }
    },

    async type() {
        this.mode = 'type';

        try {
            this.fonts ??= await Promise.all([signatureLatin, signatureLatinExt].map(async (url, index) => {
                const bytes = await (await fetch(url)).arrayBuffer();
                document.fonts.add(await new FontFace(`Signature${index}`, bytes).load());

                return btoa(Array.from(new Uint8Array(bytes), (byte) => String.fromCharCode(byte)).join(''));
            }));
        } catch {
            // offline, or the font couldn't be read: drawing still works
            this.mode = 'draw';
            window.Toaster?.error('Type Your Name couldn\'t load. Draw your signature instead.');

            return;
        }

        // a name typed while they loaded
        this.typed();
    },

    // The typed name as SVG text with the font inside it, so it looks the same on every device and on paper. Latin
    // letters only (no emoji or symbols), as the server checks; other scripts: BACKLOG "Removed, possibly wanted back"
    typed() {
        const allowed = this.name.replace(/[^\p{Script=Latin}\p{M} '’.\-]/gu, '');
        if (allowed !== this.name && Date.now() - this.warned > 3000) {
            this.warned = Date.now();
            window.Toaster?.warning('Only letters can be used in a typed signature: no emojis, numbers or symbols.');
        }
        this.name = allowed.replace(/ {2,}/g, ' ');
        const name = this.name.trim().slice(0, 100);

        if (!name || !this.fonts) {
            this.value = '';
        } else {
            const size = 48;
            const context = document.createElement('canvas').getContext('2d');
            context.font = `${size}px Signature0, Signature1`;
            const width = Math.ceil(context.measureText(name).width) + 16;
            const text = name.replace(/[&<>"]/g, (character) => `&#${character.charCodeAt(0)};`);

            this.value = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${size * 1.5}" width="${width}" height="${size * 1.5}">`
                + `<style>${this.fonts.map((font, index) => `@font-face{font-family:s${index};src:url(data:font/woff2;base64,${font})}`).join('')}</style>`
                + `<text x="8" y="${size}" font-family="s0,s1" font-size="${size}" fill="${PEN}">${text}</text></svg>`;
        }

        this.edited = true;
    },

    sync() {
        this.value = this.signaturePad.isEmpty() ? '' : this.svg();
        this.edited = true;
    },

    // The drawing cropped to its ink (a small margin for the pen's width) and to a tenth of a pixel: it fills its
    // space wherever it's shown, at about half the size
    svg() {
        const points = this.signaturePad.toData().flatMap((stroke) => stroke.points);
        const margin = 4;
        const [left, top] = ['x', 'y'].map((axis) => Math.floor(Math.min(...points.map((point) => point[axis]))) - margin);
        const [right, bottom] = ['x', 'y'].map((axis) => Math.ceil(Math.max(...points.map((point) => point[axis]))) + margin);
        const ink = `stroke="${PEN}" fill="none" stroke-linecap="round"`;

        // Each stroke's many short paths share one pen, said once on a group around them
        return this.signaturePad.toSVG()
            .replaceAll(` ${ink}`, '')
            .replace(' xmlns:xlink="http://www.w3.org/1999/xlink"', '')
            .replace(/viewBox="[^"]*" width="[^"]*" height="[^"]*">/, `viewBox="${left} ${top} ${right - left} ${bottom - top}" width="${right - left}" height="${bottom - top}"><g ${ink}>`)
            .replace('</svg>', '</g></svg>')
            .replaceAll('></path>', '/>')
            .replace(/(\d+\.\d)\d+/g, '$1')
            .replace(/\.0\b/g, '');
    },
}));

// A place on the map ({latitude, longitude, name, country} or null), chosen in a dialog: a search, this device's
// location, or the pin dragged or put with a click; the name is the town or county around it (routes/web/location).
// Like the signature, its form hears of a change once the dialog closes.
Alpine.data('locationInput', (reverse, start, addresses) => ({
    value: null,
    source: 'map',      // where it's from: address, gps or map (as the Location setter's list)
    addressId: '',      // with Address: which
    foundPlace: '',     // a place chosen in Find a Place (a remote dropdown: its value is the place, as JSON)
    busy: false,
    shown: null,        // the map, once it's on screen (map.js, loaded then)
    naming: null,       // the wait before asking the place's name, while a pin is still being moved

    init() {
        // the bound value arrives after init: an address's point reads as that address, any other as the map's
        this.$nextTick(() => {
            const at = this.value && addresses.find((address) => address.latitude === this.value.latitude && address.longitude === this.value.longitude);
            [this.source, this.addressId] = at ? ['address', String(at.id)] : [this.value || !addresses.length ? 'map' : 'address', ''];

            // watched once the starting choice is set, so only a visitor's change moves the point
            this.$watch('source', (source) => source === 'gps' ? this.locate() : source === 'address' && this.useAddress());
            this.$watch('addressId', () => this.useAddress());
        });

        const sized = new ResizeObserver(async () => {
            if (this.$refs.map.offsetWidth && !this.shown) {
                sized.disconnect();
                const { map } = await import('./map.js');
                this.shown = map(this.$refs.map, this.value, (latitude, longitude) => {
                    this.source = 'map';
                    this.set(latitude, longitude);
                }, start);
            }
        });
        sized.observe(this.$refs.map);

        this.$watch('foundPlace', (value) => {
            if (!value) return;
            const place = JSON.parse(value);
            this.set(place.latitude, place.longitude, place);
            this.foundPlace = '';
        });
    },

    useAddress() {
        const address = addresses.find((address) => String(address.id) === this.addressId) ?? addresses[0];
        if (!address) return;

        this.addressId = String(address.id);
        this.set(address.latitude, address.longitude, address);
    },

    set(latitude, longitude, place = null) {
        this.value = { latitude, longitude, name: place?.name ?? null, country: place?.country ?? null };
        place && this.shown?.move(latitude, longitude);
        changed(this.$root);
        clearTimeout(this.naming);

        if (!place) {
            this.naming = setTimeout(async () => {
                const found = (await json(`${reverse}?lat=${latitude}&lng=${longitude}`).catch(() => ({}))).place;
                // still where it was put
                if (found && this.value?.latitude === latitude && this.value?.longitude === longitude) {
                    this.value = { ...this.value, name: found.name, country: found.country };
                }
            }, 700);
        }
    },

    async locate() {
        this.busy = true;
        const coords = await position({ precise: true });
        this.busy = false;

        if (coords) {
            this.set(coords.latitude, coords.longitude);
            this.shown?.move(coords.latitude, coords.longitude);
        }
    },
}));

// The Location component (App\Livewire\Location): one list and one map. "GPS (This Device)" is kept in the browser
// (the device's, chosen unless another is), the other choices on the account ($wire.mode); `choice` reads and sets
// both. The map (map.js, made once it's on screen: a closed dialog has no size) shows $wire.point, moving its pin as
// the choice changes; a click, a drag or a place searched for makes it the chosen point. GPS is asked at once where
// `atOnce` (the directory); high accuracy, so a phone answers with GPS; the server keeps only a precise reading.
const GPS_SWITCH = 'location-gps';

/**
 * Following this device while a page with distances is open (owner, 2026-10-29): only when location is allowed
 * already (never a prompt) and GPS is on here. The browser says when it moves; a reading goes to the Location setter
 * at most once a minute, and only when it's moved 250 m or more from the last one sent (or is far more precise).
 * Paused while the tab is hidden; stopped when the page is left. One per page: the newest setter on it takes them.
 */
const FOLLOW = { every: 60000, metres: 250 };
let following = null;

const metresApart = (a, b) => {
    const rad = Math.PI / 180, dy = (b.latitude - a.latitude) * rad, dx = (b.longitude - a.longitude) * rad * Math.cos((a.latitude + b.latitude) / 2 * rad);
    return 6371000 * Math.hypot(dx, dy);
};

async function follow(wire, from) {
    if (following) {
        following.wire = wire;
        return;
    }
    if (!navigator.geolocation || !await allowed('location')) return;

    // the point already in use (a reading from the last two minutes, else none): the first new one is sent only when away from it
    const state = following = { wire, last: from, sent: from ? Date.now() : 0, watch: null };
    const reading = ({ coords }) => {
        const moved = !state.last || metresApart(state.last, coords) >= FOLLOW.metres || coords.accuracy < (state.last.accuracy ?? Infinity) / 2;
        if (!moved || Date.now() - state.sent < FOLLOW.every && state.sent) return;
        state.last = { latitude: coords.latitude, longitude: coords.longitude, accuracy: coords.accuracy };
        state.sent = Date.now();
        state.wire.located(coords.latitude, coords.longitude, coords.accuracy);
    };
    const start = () => state.watch ??= navigator.geolocation.watchPosition(reading, () => {}, { enableHighAccuracy: true, maximumAge: FOLLOW.every });
    const stop = () => {
        state.watch !== null && navigator.geolocation.clearWatch(state.watch);
        state.watch = null;
    };
    const shown = () => (document.hidden ? stop() : start());

    document.addEventListener('visibilitychange', shown);
    document.addEventListener('livewire:navigating', () => {
        stop();
        document.removeEventListener('visibilitychange', shown);
        following = null;
    }, { once: true });
    start();
}
Alpine.data('locationSetting', (atOnce, start) => ({
    enabled: (() => { try { return localStorage.getItem(GPS_SWITCH) !== 'off' } catch { return true } })(),
    foundPlace: '',     // a place chosen in Find a Place (a remote dropdown: its value is the place, as JSON)
    busy: false,
    shown: null,

    init() {
        atOnce && this.enabled && !this.$wire.fresh && this.use(true);
        // followed while it's open, where allowed already (the point in use, when it's this device's fresh reading)
        this.enabled && follow(this.$wire, this.$wire.fresh ? this.$wire.point : null);

        // the map is kept on its element: started again (a refresh), this finds it rather than make a second one there
        this.shown = this.$refs.map._locationMap ?? null;
        const sized = new ResizeObserver(async () => {
            if (this.$refs.map.offsetWidth && !this.shown) {
                sized.disconnect();
                const { map } = await import('./map.js');
                this.shown = this.$refs.map._locationMap ??= map(this.$refs.map, this.$wire.point, (latitude, longitude) => this.pick(latitude, longitude), start);
            }
        });
        sized.observe(this.$refs.map);
        // Livewire's own watch: a refresh's new point isn't seen by Alpine's
        this.$wire.$watch('point', (point) => point ? this.shown?.move(point.latitude, point.longitude, 10) : this.shown?.clear());
        // a place found: the chosen point, and the search empty again for the next
        this.$watch('foundPlace', (value) => {
            if (!value) return;
            const place = JSON.parse(value);
            this.pick(place.latitude, place.longitude, place);
            this.foundPlace = '';
        });
    },

    get choice() {
        return this.enabled ? 'gps' : this.$wire.mode;
    },

    // GPS: this device's, falling back to the account's choice; anything else: the account's, and GPS off here
    set choice(value) {
        this.gps(value === 'gps');

        if (this.enabled) {
            this.use();
        } else {
            this.$wire.forgetDevice();
            value !== this.$wire.mode && this.$wire.set('mode', value);
        }
    },

    gps(on) {
        this.enabled = on;
        try { localStorage.setItem(GPS_SWITCH, on ? 'on' : 'off') } catch {}
    },

    /** A point put on the map, or a place found: the chosen point (Chosen on the Map) */
    pick(latitude, longitude, place = null) {
        this.gps(false);
        this.$wire.choose(latitude, longitude, place?.name ?? null, place?.country ?? null);
    },

    async use(quiet = false) {
        this.busy = true;
        const coords = await position({ quiet, precise: true });
        this.busy = false;

        coords && this.$wire.located(coords.latitude, coords.longitude, coords.accuracy);
    },
}));

// The Markdown editor (ckeditor.js), its content kept in `value`.
Alpine.data('editorInput', (uploadUrl, media, actions = {}, reading = false) => ({
    value: '',

    init() {
        // The page's buttons in its toolbar (components/input/editor), those it named: Save calls the component's save,
        // the editor kept open; Delete opens its Delete dialog; Back to View Mode calls its close
        const run = {
            save: (method) => () => this.$wire[method](false),
            delete: (dialog) => () => openDialog(dialog),
            close: (method) => () => this.$wire[method](),
        };
        const buttons = Object.fromEntries(Object.entries(actions).filter(([name, as]) => run[name] && as).map(([name, as]) => [name, run[name](as)]));
        const model = {get: () => this.value, set: (value) => (this.value = value, changed(this.$root, true))};
        // After x-modelable has brought in the starting text
        this.$nextTick(() => initEditor(this.$refs.editor, model, {uploadUrl, media, actions: buttons, reading}));
    },
}));

// The site's dropdown (x-input type="select", components/input/select): options from its <option> tags
// (the text is the name, data-icon a flag or symbol for the icon column, data-short what the closed field
// shows, <optgroup> for headings). A layer like dialogs: Escape, Back and a click outside close it. With
// search, typing filters: names starting with the text first, then a whole word ("44"), then the rest,
// accents ignored. Without, typing jumps to the first option starting with the letters, as a select does.
// sheet: on phones it opens full screen instead of under the field. single: opening it closes any other open
// single dropdown, so only one is open at a time. keyboard: opening puts the cursor in the search box (bringing up
// a phone's keyboard); never in the full-screen list, which opens ready to scroll.
const plain = (text) => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
// A colour typed into a colour list's search (components/input/colour): #rgb, #rrggbb or rgb(r, g, b) → #rrggbb
const typedColour = (text) => {
    const value = text.trim().toLowerCase();
    let match;
    if ((match = value.match(/^#?([0-9a-f]{6})$/))) return '#' + match[1];
    if ((match = value.match(/^#?([0-9a-f])([0-9a-f])([0-9a-f])$/))) return '#' + match.slice(1).map((c) => c + c).join('');
    if ((match = value.match(/^rgb\(\s*(\d{1,3})[\s,]+(\d{1,3})[\s,]+(\d{1,3})\s*\)$/)) && match.slice(1).every((n) => n <= 255)) {
        return '#' + match.slice(1).map((n) => Number(n).toString(16).padStart(2, '0')).join('');
    }
    return null;
};
// What a list that takes what's typed (custom) gives for it: "new:{text}" (App\Services\BreedService::NEW)
const NEW = 'new:';
const rgb = (hex) => `rgb(${[1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)).join(', ')})`;

// An option as the list draws it; text and detail made plain once, for searching
const option = (fields) => {
    const made = { value: '', text: '', swatch: '', detail: '', icon: '', svg: '', short: '', group: '', ...fields };
    return { ...made, plain: plain(made.text), key: plain(`${made.text} ${made.detail}`) };
};
const read = (element) => option({
    value: element.value,
    text: element.text.trim(),
    swatch: element.dataset.swatch ?? '',   // a colour list's sample (components/input/colour)
    detail: element.dataset.detail ?? '',   // a second line under the name, also searched
    icon: element.dataset.icon ?? '',
    svg: element.dataset.svg ?? '',   // a data-fa icon, drawn by the select component
    short: element.dataset.short ?? '',
    group: element.parentElement.tagName === 'OPTGROUP' ? element.parentElement.label : '',
});

// What's typed, as an option of its own: a colour in a colour list, else, in a list that takes it, the text in its
// format (resources/js/format.js; the server formats it again)
const typed = (text, { colour, custom, format: kind }) => {
    const hex = colour && typedColour(text);
    if (hex) return option({ value: hex, text, swatch: hex, detail: `${hex} · ${rgb(hex)}` });
    const name = custom && formatText(text, kind);
    return name ? option({ value: NEW + name, text: name, detail: 'Not in the list: added as shown' }) : null;
};
const phone = window.matchMedia('(max-width: 639px)');

// Which way an opened list or calendar goes (components/input/select, date; still a "dropdown" when it goes up),
// kept clear of the bars that stay on screen (the phone's bottom bar; any sticky top bar):
// - it fits under the field: it opens down, nothing moves
// - it fits above: it opens up, nothing moves
// - neither: it opens down and the page scrolls it into view, the content under the field pushed down first
//   when the page is too short to scroll that far (undone on closing)
// `direction` down or up fixes the way; down still scrolls and pushes when needed. With pushing off
// (custom.dropdown_push, data-push="0"), and always in a dialog (owner, 2026-10-29: nothing in it moves), the third
// case floats: it opens towards the bigger room, its list shortened to fit there (never under the bars).
const scroller = (el) => {
    for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
        if (/(auto|scroll)/.test(getComputedStyle(node).overflowY) && node.scrollHeight > node.clientHeight) return node;
    }

    return document.scrollingElement;
};

// The part of the screen nothing covers: under any sticky or fixed top bar, above the bottom bar
const clear = () => {
    const height = window.visualViewport?.height ?? window.innerHeight;
    const bars = [...document.querySelectorAll('.sticky, .fixed')].filter((bar) => bar.offsetHeight && !bar.closest('[x-ref=panel]'))
        .map((bar) => bar.getBoundingClientRect()).filter((box) => box.width > window.innerWidth / 2);
    const top = Math.max(0, ...bars.filter((box) => box.top <= 0 && box.bottom < height / 3).map((box) => box.bottom));
    const bottom = Math.min(height, ...bars.filter((box) => box.bottom >= height - 1 && box.top > height * 2 / 3).map((box) => box.top));

    return { top: top + 8, bottom: bottom - 8 };
};

const place = (component) => {
    const { panel, trigger } = component.$refs;
    const direction = component.$root.dataset.direction ?? 'auto';
    const height = panel.offsetHeight;

    // Once per height: opening and the panel's first draw both ask. A new height starts over: down, nothing pushed
    if (!height || component.placed === height) return;
    component.placed = height;
    component.up = false;
    unplace(component);

    const field = trigger.getBoundingClientRect();
    const { top, bottom } = clear();

    const flip = () => {
        component.up = true;
        component.outline = trigger.offsetHeight + height;
    };

    if (direction === 'up') return flip();
    if (field.bottom + height <= bottom) return;
    if (direction === 'auto' && field.top - height >= top) return flip();

    const push = component.$root.dataset.push !== '0' && !trigger.closest('dialog');

    if (!push) {
        // floating: the bigger room, the list shortened to it (at least a few rows)
        const above = direction === 'auto' && field.top - top > bottom - field.bottom;
        const room = above ? field.top - top : bottom - field.bottom;
        // (a calendar has no list to shorten: it just opens the bigger way)
        const list = component.$refs.list;
        if (list) list.style.maxHeight = `${Math.max(160, room - (height - list.offsetHeight))}px`;
        component.placed = panel.offsetHeight;
        if (above) {
            component.up = true;
            component.outline = trigger.offsetHeight + panel.offsetHeight;
        }

        return;
    }

    // Down, scrolled into view: its bottom just above the bottom bar, the field never pushed off the top. A page
    // too short to scroll that far gets room under the field; a short page sits centred, so taking room moves the
    // field too: measured again until there's enough
    const box = scroller(trigger);
    let by = 0;

    // (each round on a centred page closes half the gap; a dozen leave under a pixel)
    for (let tries = 0; tries < 12; tries++) {
        const now = trigger.getBoundingClientRect();
        by = Math.min(now.bottom + height - bottom, now.top - top);
        panel.style.display = 'none';
        const left = box.scrollHeight - box.clientHeight - box.scrollTop;
        panel.style.display = '';

        if (by <= left + 1 || !push) {
            by = Math.min(by, left);
            break;
        }
        component.$root.style.marginBottom = `${(parseFloat(component.$root.style.marginBottom) || 0) + by - left}px`;
    }

    box.scrollBy({ top: by, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
};

// Closing gives back any room the panel pushed open, and a list shortened to float its full height
const unplace = (component, closing = false) => {
    component.$root.style.marginBottom = '';
    if (closing && component.$refs.list) component.$refs.list.style.maxHeight = '';
};

// Spreading openable() copies its isTop getter as a fixed value; this puts the live getter back
const withLayer = (data) => Object.defineProperty(data, 'isTop', Object.getOwnPropertyDescriptor(openable(), 'isTop'));

/**
 * The site's dropdown (components/input/select), set by one object, each part off unless given: search (a search
 * box), sheet (on phones full screen: true, false or 'auto' when cluttered), single (opening it closes the others;
 * on), keyboard (opening puts the cursor in the search box; on), multiple (pick any number), colour (a colour typed
 * can be used, components/input/colour), custom (what's typed can be used when it isn't in the list: "Use …" under
 * the results) and format (how that's written: title, the default, sentence, upper, lower or none)
 */
Alpine.data('select', ({ search = false, sheet = 'auto', single = true, keyboard = true, multiple = false, colour = false, custom = false, format = 'title', remote = null } = {}) => withLayer({
    ...openable({
        opened() {
            // From the field, bubbling to window: a date field ignores the dropdowns inside its own calendar
            single && this.$root.dispatchEvent(new CustomEvent('select-opened', { detail: this.layerId, bubbles: true }));
            this.options = remote ? this.fetched : [...this.$refs.source.options].filter((option) => !option.disabled).map(read);
            // Worked out once per opening, not by every row
            this.icons = this.options.some((option) => option.icon || option.svg || option.swatch);
            this.query = '';
            this.filter();
            // The chosen one highlighted; with nothing chosen nothing is, until an arrow or the pointer moves (owner:
            // a first row lit looked chosen)
            this.active = this.results.findIndex((option) => this.picked(option));
            // Full screen on phones: always, never, or (auto) when the list is cluttered
            this.full = phone.matches && (sheet === 'auto' ? search || this.options.length > 8 : sheet);
            this.full && layers.lock(true);
            // The outline starts at the field's height and grows with the list
            this.closing = false;
            this.problem = '';
            this.up = false;
            this.placed = 0;
            this.outline = this.$refs.trigger.offsetHeight;
            this.$nextTick(() => {
                // The search box (keyboard), else the field itself, so arrows and typing still reach the list
                search && keyboard && !this.full ? this.$refs.search.focus() : this.$refs.trigger.focus({ preventScroll: true });
                this.reveal(true);
                this.full || place(this);
            });
        },
        closed() {
            unplace(this, true);
            this.full && layers.lock(false);
            // The outline shrinks back to the field's height, then (its transitionend, or this in case it never
            // runs) the field's own ring takes over in its place
            if (!this.full) {
                this.closing = true;
                this.outline = this.$refs.trigger.offsetHeight;
                clearTimeout(this.closingTimer);
                this.closingTimer = setTimeout(() => this.closing = false, 300);
            }
            this.full = false;
        },
    }),

    value: '',
    query: '',
    active: 0,
    options: [],
    full: false,
    typed: '',
    outline: 0,
    closing: false,
    up: false,
    icons: false,
    results: [],
    fetched: [],        // remote: the server's answer to the last search
    searched: '',       // remote: what that search was for
    message: '',        // remote: searching, nothing found, or why it failed
    fallback: null,     // the value in effect while nothing's chosen (data-default), shown greyed

    init() {
        // The default kept up as the page changes it (Livewire redrawing the field with another)
        const fallback = () => this.fallback = this.$root.dataset.default ?? null;
        fallback();
        new MutationObserver(fallback).observe(this.$root, { attributes: true, attributeFilter: ['data-default'] });

        this.$watch('query', () => {
            this.message = '';
            this.filter();
            // Searching: the best match is ready for Enter
            this.active = this.query.trim() ? 0 : -1;
        });

        // The green outline's height: the field and its list together, kept up as the list grows or shrinks
        // (the panel is held here: $refs is gone once a page change has taken the dropdown away)
        const panel = this.$refs.panel, trigger = this.$refs.trigger;
        new ResizeObserver(() => {
            if (!this.show) return;
            // Grown since it was placed (the calendar drawn on a slow phone): placed again; then the ring follows
            this.full || place(this);
            this.$nextTick(() => this.outline = this.up ? trigger.offsetHeight + panel.offsetHeight : panel.offsetTop + panel.offsetHeight);
        }).observe(panel);

        // Another single dropdown opened: this one makes way
        single && window.addEventListener('select-opened', (event) => event.detail !== this.layerId && this.close());
    },

    // The chosen option, or null (then the placeholder shows); several picked: their names together
    get chosen() {
        if (remote) {
            return this.fetched.find((option) => this.picked(option)) ?? null;
        }

        const options = [...this.$refs.source.options].filter((option) => !option.disabled && this.picked(option)).map(read);

        // Something typed in that isn't in the list: shown as itself
        if (!options.length && this.value) {
            const value = String(this.value);
            return value.startsWith(NEW) ? option({ text: value.slice(NEW.length) }) : colour ? typed(value, { colour }) : null;
        }

        return !options.length ? null : multiple ? { text: options.map((option) => option.text).join(', ') } : options[0];
    },

    // Something chosen (an optional dropdown can be cleared then: its ×). A value the list doesn't hold shows the
    // placeholder, so there's nothing to clear; a remote list may not have fetched it yet
    get filled() {
        if (Array.isArray(this.value)) return this.value.length > 0;
        return remote ? this.value !== '' && this.value !== null && this.value !== undefined : this.chosen !== null;
    },

    clear() {
        this.value = multiple ? [] : '';
        changed(this.$root);
        this.close(this.$refs.trigger);
    },

    // Whether an option is the chosen one (or, picking several, one of them)
    picked(option) {
        return multiple ? [].concat(this.value || []).some((value) => value == option.value) : option.value == this.value;
    },

    // Nothing chosen: the default's option, in effect without being chosen (so it changes when the default does)
    get standing() {
        if (this.chosen || this.fallback === null || multiple || remote) return null;
        const option = [...this.$refs.source.options].find((option) => !option.disabled && option.value === this.fallback);

        return option ? read(option) : null;
    },

    get placeholder() {
        // The default's name, else the list's own disabled option ("Select Registry"), else "Select {the field's name}"
        return this.standing ? (this.standing.short || this.standing.text)
            : [...this.$refs.source.options].find((option) => option.disabled)?.text.trim() ?? this.$root.dataset.placeholder ?? 'Choose…';
    },

    // What the closed field shows in its icon spot: a flag or a one-character symbol (€), the default's too; longer
    // ones (KM, CHF) don't fit
    get badge() {
        const icon = (this.chosen ?? this.standing)?.icon ?? '';

        return [...icon].length === 1 || /^\p{Regional_Indicator}{2}$/u.test(icon) ? icon : '';
    },

    // The options shown: all, or those matching the search, best first within each group, the groups in their order
    // with their names (owner, 2026-10-25). Worked out once per change, since every row reads them (its heading)
    filter() {
        const query = plain(this.query.trim()).replace(/^\+/, '');
        // remote: the server's answer as it is (asked on Enter, never as it's typed)
        if (!query || remote) return this.results = this.options;

        const rank = (option) => {
            const words = option.plain.split(/[\s+(),-]+/);
            return option.plain.startsWith(query) ? 0 : words.includes(query) ? 1 : words.some((word) => word.startsWith(query)) ? 2 : 3;
        };
        const groups = [...new Set(this.options.map((option) => option.group))];
        const results = this.options.filter((option) => option.key.includes(query))
            .sort((a, b) => groups.indexOf(a.group) - groups.indexOf(b.group) || rank(a) - rank(b));

        // What's typed, offered as it is: a colour first; a name last, unless it's exactly one of the list's
        const own = typed(this.query, { colour, custom, format });
        this.results = !own ? results
            : own.swatch ? (this.options.some((option) => option.swatch === own.swatch) ? results : [{ ...own, text: `Use ${own.swatch}` }, ...results])
            : this.options.some((option) => option.plain === own.plain) ? results : [...results, { ...own, text: `Use “${own.text}”` }];
    },

    // A heading above the first option of each group, while searching too
    heading(index) {
        const group = this.results[index].group;

        return group && group !== this.results[index - 1]?.group ? group : '';
    },

    move(step) {
        if (!this.show) return this.open();

        if (this.results.length) {
            // From nothing highlighted: the first (down) or the last (up)
            this.active = this.active < 0 ? (step > 0 ? 0 : this.results.length - 1)
                : (this.active + step + this.results.length) % this.results.length;
            this.reveal();
        }
    },

    // Enter in the search box: a remote list asks the server for what's typed (once per text), else the highlighted one
    enter() {
        remote && this.query.trim() && this.query.trim() !== this.searched ? this.find() : this.choose(this.active);
    },

    // remote: the options for what's typed, from the server ({options: [{value, text, detail}]}, or a message)
    async find() {
        const text = this.searched = this.query.trim();
        // nothing to pick while it's asked (Enter again would take one of the last answer's)
        [this.fetched, this.options, this.message] = [[], [], 'Searching…'];
        this.filter();

        try {
            const answer = await json(`${remote}${remote.includes('?') ? '&' : '?'}q=${encodeURIComponent(text)}`);
            if (text !== this.searched) return;   // a newer search was asked meanwhile
            this.fetched = this.options = (answer.options ?? []).map((fields) => option(fields));
            this.message = this.fetched.length ? '' : 'Nothing found. Try other words.';
        } catch (error) {
            if (text !== this.searched) return;
            this.message = error.message === 'network' ? 'The search couldn\'t be reached.' : error.message;
        }

        this.filter();
        this.active = this.results.length ? 0 : -1;
    },

    choose(index) {
        const option = this.results[index];
        if (!option) return;

        if (multiple) {
            // Ticks or unticks it; the list stays open for the next
            const values = [].concat(this.value || []);
            this.value = this.picked(option) ? values.filter((value) => value != option.value) : [...values, option.value];
            changed(this.$root);
            this.active = index;
        } else {
            this.value = option.value;
            changed(this.$root);
            this.close(this.$refs.trigger);
        }
    },

    // Without a search box, typing jumps to the first option starting with the letters typed
    jump(event) {
        if (search || event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return;

        clearTimeout(this.typing);
        this.typed += plain(event.key);
        this.typing = setTimeout(() => this.typed = '', 700);

        const options = this.show ? this.results : [...this.$refs.source.options].filter((option) => !option.disabled).map(read);
        const index = options.findIndex((option) => option.plain.startsWith(this.typed));

        if (index === -1) return;
        this.show ? (this.active = index, this.reveal()) : multiple || (this.value = options[index].value, changed(this.$root));
    },

    /**
     * Scrolls the list (never the page) to the active option: into the middle on opening, as far as the
     * list allows, else just into view.
     */
    reveal(middle = false) {
        this.$nextTick(() => {
            const list = this.$refs.list;
            const option = list.querySelectorAll('[role=option]')[this.active];
            if (!option) return;

            // From the list's own top (it's positioned), unaffected by the opening animation's scale
            const top = option.offsetTop;
            if (middle) {
                list.scrollTop = top - (list.clientHeight - option.offsetHeight) / 2;
            } else if (top < list.scrollTop) {
                list.scrollTop = top;
            } else if (top + option.offsetHeight > list.scrollTop + list.clientHeight) {
                list.scrollTop = top + option.offsetHeight - list.clientHeight;
            }
        });
    },
}));

// The site's time field (x-input type="time", components/input/time): the site's dropdowns for the hour and
// minute (and AM/PM on the 12-hour clock); the time is 14:30 either way, empty until a part is chosen.
Alpine.data('timeInput', (hour12 = false) => ({
    // Not `value`: the dropdowns inside have their own, and Alpine lets a getter here see theirs first
    time: '',

    get hasTime() {
        return typeof this.time === 'string' && /^\d{2}:\d{2}/.test(this.time);
    },

    get hours() {
        return this.hasTime ? Number(this.time.slice(0, 2)) : 12;
    },

    get hour() {
        return !this.hasTime ? '' : hour12 ? String(this.hours % 12 || 12) : this.time.slice(0, 2);
    },

    set hour(hour) {
        this.put(hour12 ? Number(hour) % 12 + (this.period === 'PM' ? 12 : 0) : Number(hour), this.minute || '00');
    },

    get minute() {
        return this.hasTime ? this.time.slice(3, 5) : '';
    },

    set minute(minute) {
        this.put(this.hours, minute);
    },

    get period() {
        return this.hours >= 12 ? 'PM' : 'AM';
    },

    set period(period) {
        this.put(this.hours % 12 + (period === 'PM' ? 12 : 0), this.minute || '00');
    },

    put(hours, minute) {
        this.time = `${String(hours).padStart(2, '0')}:${minute}`;
    },
}));

// The site's date field (x-input type="date" or "datetime-local", components/input/date): a field like the
// dropdown's that opens into a calendar (Cally, loaded when first needed), under the field or full screen
// on phones. Its value is what the browser's own date field gives: 2026-10-20, or 2026-10-20T14:30 with a
// time. The month heading opens a list of years, then months, to get to a birth date quickly.
let cally = null;
const iso = (date) => date.toISOString().slice(0, 10);
// Today where the visitor is (toISOString would give yesterday's date just after midnight in Ireland's summer)
const today = () => iso(new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth(), new Date().getDate())));

Alpine.data('dateInput', (time = false, dayFirst = true, sheet = true, single = true, range = [1950, 2040], hour12 = false, limits = [null, null]) => withLayer({
    ...openable({
        opened() {
            // From the field, bubbling to window: a date field ignores the dropdowns inside its own calendar
            single && this.$root.dispatchEvent(new CustomEvent('select-opened', { detail: this.layerId, bubbles: true }));
            this.view = 'days';
            this.page = (this.day || today()).slice(0, 7);
            this.full = sheet && phone.matches;
            this.full && layers.lock(true);
            this.closing = false;
            this.up = false;
            this.placed = 0;
            this.outline = this.$refs.trigger.offsetHeight;
            this.$nextTick(() => this.$refs.calendar?.focus?.());
        },
        closed() {
            unplace(this);
            this.full && layers.lock(false);
            if (!this.full) {
                this.closing = true;
                this.outline = this.$refs.trigger.offsetHeight;
                clearTimeout(this.closingTimer);
                this.closingTimer = setTimeout(() => this.closing = false, 300);
            }
            this.full = false;
        },
    }),

    value: '',
    view: 'days',   // days, years or months
    page: '',       // the month shown: 2026-10
    full: false,
    outline: 0,
    closing: false,
    up: false,
    ready: false,
    problem: '',    // why the day tapped can't be chosen, said in the calendar (the field's own errors sit behind it)
    flashing: false,

    async init() {
        const panel = this.$refs.panel, trigger = this.$refs.trigger;
        new ResizeObserver(() => {
            if (!this.show) return;
            // Grown since it was placed (the calendar drawn on a slow phone): placed again; then the ring follows
            this.full || place(this);
            this.$nextTick(() => this.outline = this.up ? trigger.offsetHeight + panel.offsetHeight : panel.offsetTop + panel.offsetHeight);
        }).observe(panel);
        single && window.addEventListener('select-opened', (event) => event.detail !== this.layerId && !this.$root.contains(event.target) && this.close());

        // The years open with the chosen one in the middle: once the list has a size, it's showing
        const years = this.$refs.years;
        new ResizeObserver(() => {
            const year = years.querySelector('[aria-current=true]');
            year && years.clientHeight && (years.scrollTop = year.offsetTop - (years.clientHeight - year.offsetHeight) / 2);
        }).observe(years);

        // The calendar is drawn once Cally has defined it, so its value and focus reach the component
        cally ??= import('cally');
        await cally;
        this.ready = true;
    },

    // Until x-modelable has brought it in, value isn't a date yet
    get day() {
        return typeof this.value === 'string' ? this.value.slice(0, 10) : '';
    },

    get clock() {
        return time && typeof this.value === 'string' ? this.value.slice(11, 16) : '';
    },

    // As the visitor writes dates (31/12/2026 or 12/31/2026), with the time after a comma
    get text() {
        return this.day ? this.written(this.day) + (this.clock ? `, ${this.clockText}` : '') : '';
    },

    written(day) {
        const [year, month, date] = day.split('-');

        return dayFirst ? `${date}/${month}/${year}` : `${month}/${date}/${year}`;
    },

    // Outside the field's min and max (owner, 2026-11-02: those days are tappable, so a tap can say why not)
    outOfRange(day) {
        return Boolean(day && ((limits[0] && day < limits[0]) || (limits[1] && day > limits[1])));
    },

    // Cally greys those days out, still tappable (its own min and max would make them dead buttons)
    limit(calendar) {
        calendar.isDateDisallowed = (date) => this.outOfRange(iso(date));
    },

    // A day that can't be chosen: the ring flashes red and the calendar says which can
    refuse() {
        const [min, max] = limits.map((day) => day && this.written(day));
        this.problem = min && max ? `Choose a date from ${min} to ${max}.` : max ? `Choose a date on or before ${max}.` : `Choose a date on or after ${min}.`;
        this.flashing = true;
        clearTimeout(this.flashTimer);
        this.flashTimer = setTimeout(() => this.flashing = false, 700);
    },

    // A tap on the calendar: on a day it won't take (Cally's "disallowed"), refused
    tapped(event) {
        event.composedPath().some((element) => element.part?.contains?.('disallowed')) && this.refuse();
    },

    // 14:30, or 2:30 PM on the 12-hour clock (the time preference)
    get clockText() {
        const hours = Number(this.clock.slice(0, 2));

        return hour12 ? `${hours % 12 || 12}:${this.clock.slice(3, 5)} ${hours >= 12 ? 'PM' : 'AM'}` : this.clock;
    },

    // The time's dropdowns (components/input/time) read and change the time through this
    get clockValue() {
        return this.clock;
    },

    set clockValue(clock) {
        this.setTime(clock);
    },

    get heading() {
        const [year, month] = this.page.split('-');

        return this.view === 'years' ? 'Year' : this.view === 'months' ? year
            : new Date(Date.UTC(year, month - 1)).toLocaleString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
    },

    get years() {
        return Array.from({ length: range[1] - range[0] + 1 }, (_, index) => range[1] - index);
    },

    months: Array.from({ length: 12 }, (_, index) => new Date(Date.UTC(2000, index)).toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' })),

    set(day) {
        this.value = day && time ? `${day}T${this.clock || '12:00'}` : day;
        changed(this.$root);
    },

    today,

    pick(day) {
        if (this.outOfRange(day)) {
            this.refuse();

            return;
        }

        this.problem = '';
        this.set(day);
        time || this.close(this.$refs.trigger);
    },

    setTime(clock) {
        this.value = `${this.day || today()}T${clock || '00:00'}`;
        changed(this.$root);
    },

    // The month heading: days → years → months → days
    zoom() {
        this.view = this.view === 'days' ? 'years' : 'days';
    },

    chooseYear(year) {
        this.page = `${year}-${this.page.slice(5, 7)}`;
        this.view = 'months';
    },

    chooseMonth(index) {
        this.page = `${this.page.slice(0, 4)}-${String(index + 1).padStart(2, '0')}`;
        this.view = 'days';
    },

    // The day the calendar shows and keys move from: the chosen one in this month, else the 1st
    get focus() {
        return this.day.startsWith(this.page) ? this.day : `${this.page}-01`;
    },

    // The arrows: a month at a time, a year at a time while choosing the month
    step(by) {
        const [year, month] = this.page.split('-').map(Number);
        const date = new Date(Date.UTC(year, month - 1 + (this.view === 'months' ? by * 12 : by)));
        this.page = iso(date).slice(0, 7);
    },

    // Keys moving the calendar's focus into another month: the heading follows
    follow(date) {
        this.page = iso(date).slice(0, 7);
    },
}));
